import { useEffect, useMemo, useRef, useState } from 'react';
import { listLandingVideosPublic } from '../../legacy/api/landingVideos';
import { resolveMediaUrl } from '../../utils/resolveMediaUrl';
import styles from './WhyHomevilasVideoCarousel.module.css';

const AUTOPLAY_MS = 7200;
const PREVIEW_FRAME_TIME = 0.16;
const SWIPE_THRESHOLD = 48;

const safeArray = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const buildWarmSet = (index, total) => {
  const next = new Set();
  if (!total) return next;
  next.add(index);
  if (total > 1) next.add((index + 1) % total);
  if (total > 2) next.add((index - 1 + total) % total);
  return next;
};

const getRelativeOffset = (index, activeIndex, total) => {
  if (!total) return 0;
  let offset = index - activeIndex;
  if (offset > total / 2) offset -= total;
  if (offset < -total / 2) offset += total;
  return offset;
};

const getPositionClass = (offset) => {
  if (offset === 0) return styles.slideActive;
  if (offset === -1) return styles.slidePrev;
  if (offset === 1) return styles.slideNext;
  if (offset === -2) return styles.slideEdgePrev;
  if (offset === 2) return styles.slideEdgeNext;
  return offset < 0 ? styles.slideHiddenLeft : styles.slideHiddenRight;
};

const getVideoMime = (url) =>
  String(url || '').toLowerCase().endsWith('.webm') ? 'video/webm' : 'video/mp4';

const freezePreviewFrame = (event) => {
  const video = event.currentTarget;
  if (!video) return;
  video.muted = true;
  video.pause();
  const duration = Number(video.duration);
  const nextFrame =
    Number.isFinite(duration) && duration > PREVIEW_FRAME_TIME
      ? PREVIEW_FRAME_TIME
      : 0;
  try {
    video.currentTime = nextFrame;
  } catch (_) {
    // Ignore browsers that block seeks before enough data is loaded.
  }
};

const toSlide = (item, index) => {
  const title = String(item?.title || '').trim() || `Homevilas reel ${index + 1}`;
  const caption = String(
    item?.short_caption || item?.shortCaption || item?.description || item?.caption || ''
  ).trim();
  const thumbnailUrl = resolveMediaUrl(
    item?.thumbnail_url || item?.thumbnailUrl || item?.poster_image_url || item?.posterImageUrl || ''
  );
  const videoUrl = resolveMediaUrl(item?.video_url || item?.videoUrl || '');
  const instagramUrl = String(item?.instagram_url || item?.instagramUrl || '').trim();
  const instagramEmbedUrl = String(
    item?.instagram_embed_url || item?.instagramEmbedUrl || ''
  ).trim();
  const sourceType =
    String(item?.source_type || item?.sourceType || '').trim() ||
    (instagramUrl ? 'instagram' : 'file');

  return {
    id: item?.id || `landing-video-${index + 1}`,
    title,
    caption,
    thumbnailUrl,
    videoUrl,
    instagramUrl,
    instagramEmbedUrl,
    sourceType,
    isActive: item?.is_active !== false && item?.isActive !== false,
    displayOrder: Number(
      item?.display_order ?? item?.displayOrder ?? item?.sort_order ?? item?.sortOrder ?? index + 1
    ) || index + 1,
  };
};

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    {children}
  </svg>
);

const ArrowLeftIcon = () => (
  <Icon>
    <path d="M14.5 6.5L9 12l5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

const ArrowRightIcon = () => (
  <Icon>
    <path d="M9.5 6.5L15 12l-5.5 5.5" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </Icon>
);

const PlayIcon = () => (
  <Icon>
    <path d="M9 7.4v9.2l7.4-4.6L9 7.4z" fill="currentColor" />
  </Icon>
);

const PauseIcon = () => (
  <Icon>
    <path d="M8.2 7.2h2.6v9.6H8.2zM13.2 7.2h2.6v9.6h-2.6z" fill="currentColor" />
  </Icon>
);

const VolumeIcon = ({ muted }) => (
  <Icon>
    <path d="M5.6 14.4H8.5l4-3.3v6l-4-2.7H5.6z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    {muted ? (
      <path d="M15.4 9.2l4.2 5.6M19.6 9.2l-4.2 5.6" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    ) : (
      <>
        <path d="M16.3 9.2c1 .7 1.6 1.7 1.6 2.8s-.6 2.1-1.6 2.8" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M18.2 6.8c1.7 1.3 2.8 3.1 2.8 5.2s-1.1 3.9-2.8 5.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </>
    )}
  </Icon>
);

const InstagramIcon = () => (
  <Icon>
    <rect x="4.5" y="4.5" width="15" height="15" rx="4.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="12" cy="12" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.8" />
    <circle cx="17.1" cy="6.9" r="1" fill="currentColor" />
  </Icon>
);

function FallbackGrid({ items = [] }) {
  const entries = safeArray(items).filter((item) => item && (item.title || item.desc || item.text));
  if (!entries.length) return null;

  return (
    <div className={styles.fallbackGrid}>
      {entries.map((item, index) => {
        const backgroundUrl = resolveMediaUrl(
          item?.background_image_url || item?.backgroundImageUrl || item?.backgroundImage || ''
        );

        return (
          <article
            key={item.id || item.title || `why-homevilas-fallback-${index + 1}`}
            className={styles.fallbackCard}
            style={backgroundUrl ? { backgroundImage: `url(${backgroundUrl})` } : undefined}
          >
            {backgroundUrl ? <span className={styles.fallbackCardShade} aria-hidden="true" /> : null}
            <div className={styles.fallbackCardContent}>
              <h3>{item.title}</h3>
              <p>{item.desc || item.text}</p>
            </div>
          </article>
        );
      })}
    </div>
  );
}

export default function WhyHomevilasVideoCarousel({
  eyebrow = '',
  title = '',
  subtitle = '',
  videos,
  fallbackItems = [],
  ambientImage = '',
}) {
  const hasExternalVideos = Array.isArray(videos);
  const [remoteVideos, setRemoteVideos] = useState([]);
  const [loading, setLoading] = useState(!hasExternalVideos);
  const [fetchError, setFetchError] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const [isMuted, setIsMuted] = useState(true);
  const [isPausedByUser, setIsPausedByUser] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [loadedSlides, setLoadedSlides] = useState(() => new Set([0]));
  const [touchStartX, setTouchStartX] = useState(null);
  const [isInViewport, setIsInViewport] = useState(false);
  const [isDocumentVisible, setIsDocumentVisible] = useState(
    () => (typeof document === 'undefined' ? true : !document.hidden)
  );

  const stageRef = useRef(null);
  const videoRefs = useRef([]);

  useEffect(() => {
    if (hasExternalVideos) {
      setRemoteVideos([]);
      setLoading(false);
      setFetchError('');
      return;
    }

    let cancelled = false;

    const loadLandingVideos = async () => {
      setLoading(true);
      setFetchError('');
      try {
        const res = await listLandingVideosPublic();
        const list = res?.data || res || [];
        if (!cancelled) {
          setRemoteVideos(Array.isArray(list) ? list : []);
        }
      } catch (_) {
        if (!cancelled) {
          setRemoteVideos([]);
          setFetchError('Fresh reels are being curated right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadLandingVideos();

    return () => {
      cancelled = true;
    };
  }, [hasExternalVideos]);

  const normalizedVideos = useMemo(() => {
    const source = hasExternalVideos ? videos : remoteVideos;
    return safeArray(source)
      .map(toSlide)
      .filter((item) => item.isActive && (item.videoUrl || item.instagramUrl))
      .sort(
        (a, b) =>
          Number(a.displayOrder || 0) - Number(b.displayOrder || 0) ||
          String(a.id).localeCompare(String(b.id))
      );
  }, [hasExternalVideos, remoteVideos, videos]);

  const total = normalizedVideos.length;
  const activeSlide = normalizedVideos[activeIndex] || null;

  useEffect(() => {
    setActiveIndex(0);
    setIsPausedByUser(true);
    setLoadedSlides(buildWarmSet(0, total));
  }, [total]);

  useEffect(() => {
    if (!total) return;
    setLoadedSlides((current) => {
      const next = new Set(current);
      buildWarmSet(activeIndex, total).forEach((value) => next.add(value));
      return next;
    });
  }, [activeIndex, total]);

  useEffect(() => {
    if (total <= 1 || isHovered || !isPausedByUser || !isInViewport || !isDocumentVisible) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % total);
    }, AUTOPLAY_MS);

    return () => window.clearInterval(timer);
  }, [isDocumentVisible, isHovered, isInViewport, isPausedByUser, total]);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const handleVisibility = () => setIsDocumentVisible(!document.hidden);
    handleVisibility();
    document.addEventListener('visibilitychange', handleVisibility);
    window.addEventListener('focus', handleVisibility);
    window.addEventListener('blur', handleVisibility);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleVisibility);
      window.removeEventListener('blur', handleVisibility);
    };
  }, []);

  useEffect(() => {
    const target = stageRef.current;
    if (!target || typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(Boolean(entry?.isIntersecting) && entry.intersectionRatio >= 0.45);
      },
      { threshold: [0, 0.25, 0.45, 0.7, 1] }
    );

    observer.observe(target);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const shouldPause = isPausedByUser || !isInViewport || !isDocumentVisible;
    const videoNodes = videoRefs.current;

    videoNodes.forEach((video, index) => {
      if (!video) return;

      const isCurrent = index === activeIndex;
      video.muted = isCurrent ? isMuted : true;

      if (!isCurrent || shouldPause) {
        video.pause();
        if (!isCurrent) {
          try {
            video.currentTime = 0;
          } catch (_) {
            // Ignore seek resets on metadata-only loads.
          }
        }
        return;
      }

      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    });

    return () => {
      videoNodes.forEach((video) => video?.pause());
    };
  }, [activeIndex, isDocumentVisible, isInViewport, isMuted, isPausedByUser]);

  const goTo = (index) => {
    if (!total) return;
    const nextIndex = ((index % total) + total) % total;
    if (nextIndex === activeIndex) return;
    setIsPausedByUser(true);
    setActiveIndex(nextIndex);
  };

  const handleTouchStart = (event) => {
    setTouchStartX(event.changedTouches?.[0]?.clientX ?? null);
  };

  const handleTouchEnd = (event) => {
    if (touchStartX === null || total <= 1) return;
    const diff = (event.changedTouches?.[0]?.clientX ?? 0) - touchStartX;
    if (Math.abs(diff) >= SWIPE_THRESHOLD) {
      goTo(activeIndex + (diff < 0 ? 1 : -1));
    }
    setTouchStartX(null);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      goTo(activeIndex + 1);
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      goTo(activeIndex - 1);
    }
  };

  const handleSlideClick = (index, isActiveSlide) => {
    const slide = normalizedVideos[index];

    if (!slide) {
      return;
    }

    if (isActiveSlide) {
      if (slide.sourceType === 'instagram') {
        setIsPausedByUser((current) => !current);
        return;
      }

      const video = videoRefs.current[index];
      const shouldPlay = Boolean(video?.paused) || isPausedByUser;

      if (!video) {
        setIsPausedByUser(!shouldPlay);
        return;
      }

      if (shouldPlay) {
        setIsPausedByUser(false);
        video.muted = isMuted;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
        return;
      }

      video.pause();
      setIsPausedByUser(true);
      return;
    }

    goTo(index);
  };

  const renderSlideMedia = (slide, index, isActiveSlide, shouldLoad) => {
    if (!shouldLoad) {
      return <div className={styles.mediaFallback} />;
    }

    if (slide.sourceType === 'instagram') {
      if (isActiveSlide && !isPausedByUser && slide.instagramEmbedUrl) {
        return (
          <iframe
            className={styles.mediaEmbed}
            src={slide.instagramEmbedUrl}
            title={slide.title}
            loading="lazy"
            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
            sandbox="allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-forms"
          />
        );
      }

      if (slide.thumbnailUrl) {
        return <img src={slide.thumbnailUrl} alt={slide.title} className={styles.media} loading="lazy" />;
      }

      return (
        <div className={styles.instagramPlaceholder}>
          <InstagramIcon />
          <span>Instagram Reel</span>
        </div>
      );
    }

    if (isActiveSlide) {
      return (
        <video
          ref={(node) => {
            videoRefs.current[index] = node;
          }}
          className={styles.media}
          poster={slide.thumbnailUrl || undefined}
          playsInline
          preload="metadata"
          onEnded={() => goTo(index + 1)}
        >
          <source src={slide.videoUrl} type={getVideoMime(slide.videoUrl)} />
        </video>
      );
    }

    if (slide.thumbnailUrl) {
      return <img src={slide.thumbnailUrl} alt={slide.title} className={styles.media} loading="lazy" />;
    }

    return (
      <video
        className={styles.media}
        muted
        playsInline
        preload="metadata"
        onLoadedData={freezePreviewFrame}
        onSeeked={(event) => event.currentTarget.pause()}
      >
        <source src={slide.videoUrl} type={getVideoMime(slide.videoUrl)} />
      </video>
    );
  };

  return (
    <section
      className={styles.section}
      aria-label={title || 'Why Homevilas video carousel'}
      style={ambientImage ? { '--why-homevilas-ambient': `url(${resolveMediaUrl(ambientImage)})` } : undefined}
    >
      <div className={styles.backdrop} aria-hidden="true" />
      <div className={styles.inner}>
        <div className={styles.headerRow}>
          <div className={styles.header}>
            {eyebrow ? <div className={styles.eyebrow}>{eyebrow}</div> : null}
            {title ? <h2 className={styles.title}>{title}</h2> : null}
            {subtitle ? <p className={styles.subtitle}>{subtitle}</p> : null}
          </div>
          <div className={styles.headerPills}>
            <span className={styles.headerPill}>
              {loading ? 'Loading reels' : total ? `${total} live reels` : 'Reels coming soon'}
            </span>
            {activeSlide?.sourceType ? (
              <span className={`${styles.headerPill} ${styles.headerPillMuted}`}>
                {activeSlide.sourceType === 'instagram' ? 'Instagram reel ready' : 'Tap front reel to play'}
              </span>
            ) : null}
          </div>
        </div>

        {loading ? (
          <div className={styles.skeletonShell}>
            <div className={`${styles.skeletonCard} ${styles.skeletonCardLeft}`} />
            <div className={`${styles.skeletonCard} ${styles.skeletonCardCenter}`} />
            <div className={`${styles.skeletonCard} ${styles.skeletonCardRight}`} />
          </div>
        ) : total ? (
          <div
            ref={stageRef}
            className={styles.shell}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onKeyDown={handleKeyDown}
            tabIndex={0}
          >
            <button
              type="button"
              className={`${styles.navButton} ${styles.navPrev}`}
              onClick={() => goTo(activeIndex - 1)}
              aria-label="Previous reel"
              disabled={total <= 1}
            >
              <ArrowLeftIcon />
            </button>

            <div className={styles.stage}>
              {normalizedVideos.map((slide, index) => {
                const offset = getRelativeOffset(index, activeIndex, total);
                const isActiveSlide = offset === 0;
                const shouldLoad = loadedSlides.has(index) || isActiveSlide;
                const isInstagramSlide = slide.sourceType === 'instagram';

                return (
                  <article
                    key={slide.id}
                    className={[styles.slide, getPositionClass(offset)].join(' ')}
                    onClick={() => handleSlideClick(index, isActiveSlide)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        handleSlideClick(index, isActiveSlide);
                      }
                    }}
                    aria-hidden={!isActiveSlide && Math.abs(offset) > 1}
                    role="button"
                    tabIndex={0}
                  >
                    <div className={styles.card}>
                      <div className={styles.mediaWrap}>
                        {renderSlideMedia(slide, index, isActiveSlide, shouldLoad)}
                        <div className={styles.mediaShade} />
                      </div>

                      {isActiveSlide ? (
                        <div className={styles.cardActions}>
                          <button
                            type="button"
                            className={`${styles.inlineControl} ${styles.inlinePlayControl}`}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleSlideClick(index, true);
                            }}
                            aria-label={isPausedByUser ? 'Play active reel' : 'Pause active reel'}
                          >
                            {isPausedByUser ? <PlayIcon /> : <PauseIcon />}
                            <span>{isPausedByUser ? 'Play' : 'Pause'}</span>
                          </button>

                          <div className={styles.inlineUtilityGroup}>
                            <button
                              type="button"
                              className={styles.inlineControl}
                              onClick={(event) => {
                                event.stopPropagation();
                                if (!isInstagramSlide) {
                                  setIsMuted((current) => !current);
                                }
                              }}
                              aria-label={
                                isInstagramSlide
                                  ? 'Instagram reels manage audio inside Instagram'
                                  : isMuted
                                    ? 'Unmute reel'
                                    : 'Mute reel'
                              }
                              disabled={isInstagramSlide}
                            >
                              <VolumeIcon muted={isInstagramSlide || isMuted} />
                            </button>

                            {slide.instagramUrl ? (
                              <a
                                className={styles.inlineControl}
                                href={slide.instagramUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={(event) => event.stopPropagation()}
                                aria-label="Open Instagram reel"
                              >
                                <InstagramIcon />
                              </a>
                            ) : null}
                          </div>
                        </div>
                      ) : null}

                      <div className={styles.copy}>
                        <div className={styles.copyTop}>
                          <span className={styles.kicker}>
                            {slide.sourceType === 'instagram' ? 'Instagram reel' : 'Homevilas original'}
                          </span>
                          <span className={styles.countBadge}>
                            {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
                          </span>
                        </div>
                        <h3>{slide.title}</h3>
                        <p>{slide.caption || 'Luxury stays, memorable moments, and a polished guest journey.'}</p>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>

            <button
              type="button"
              className={`${styles.navButton} ${styles.navNext}`}
              onClick={() => goTo(activeIndex + 1)}
              aria-label="Next reel"
              disabled={total <= 1}
            >
              <ArrowRightIcon />
            </button>

            <div className={styles.pagination}>
              {normalizedVideos.map((slide, index) => (
                <button
                  key={`${slide.id}-dot`}
                  type="button"
                  className={`${styles.paginationDot} ${index === activeIndex ? styles.paginationDotActive : ''}`}
                  onClick={() => goTo(index)}
                  aria-label={`Show ${slide.title}`}
                />
              ))}
            </div>
          </div>
        ) : (
          <div className={styles.emptyShell}>
            <div className={styles.emptyCard}>
              <p className={styles.emptyEyebrow}>Video testimonials</p>
              <h3>Fresh reels are on the way.</h3>
              <p>
                {fetchError || 'No active videos are published yet. Once the admin team adds reels, they will appear here automatically.'}
              </p>
            </div>
            <FallbackGrid items={fallbackItems} />
          </div>
        )}
      </div>
    </section>
  );
}
